import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router';
import { createPortal } from 'react-dom';
import { Cpu } from 'react-feather';
import { verifyAccess, truncateData, getModuleCrudPermissions } from '../../../../services';
import { llmDetailsProps, modelsTypes, providersTypes } from './llm.props.ts';
import {
  defaultLlmExtendedFields,
  displayRoutingBand,
  ROUTING_BAND_SCORE_HINT,
} from '../model-utils.ts';
import { getModels, getProviders } from '../services/llm.ts';
import LlmListToolbar from './components/llm-list-toolbar.tsx';
import {
  DEFAULT_LLM_SORT_PRESET,
  LLM_SORT_PRESETS,
  LLM_STATUS_FILTER_OPTIONS,
} from './data/llm-list-filters.ts';
import { usePageHeader } from '../../user';
import ErrorFallback from '../../../../components/error-fallback/index.tsx';
import { CustomTable, Tooltip } from '../../../../components';
import type { ColumnDef } from '../../../../components';
import { AddLLMDesc, AddLLMTitle, EditLLMDesc, EditLLMTitle } from './data/constants.ts';
import Logout from '../../../../auth/protected/session-expire-modal/index.tsx';
import { tableFunctionsProps } from '../../user/pages/users/user.props.ts';
import CreateOrUpdateLLM from './add-edit-llm.tsx';
import AddProvider from './add-provider.tsx';
import DeleteModel from './delete-llm.tsx';
import makeToast from '../../../../components/toaster/index.tsx';

type LLMModalType = 'none' | 'add-edit' | 'delete' | 'add-provider';

const emptyLLM: llmDetailsProps = {
  id: 0,
  provider_id: 1,
  name: '',
  description: '',
  input_price_per_million: undefined,
  output_price_per_million: undefined,
  ...defaultLlmExtendedFields(),
};

function LLMModule() {
  const navigate = useNavigate();
  const [logout, setLogout] = useState<boolean>(false);
  const [showError, setShowError] = useState(false);
  const [modalType, setModalType] = useState<LLMModalType>('none');
  const [modalInfo, setModalInfo] = useState({ modalTitle: '', modalDesc: '' });
  const [dataToEdit, setDataToEdit] = useState<llmDetailsProps>(emptyLLM);
  const [searchQuery, setSearchQuery] = useState('');
  const [providerFilter, setProviderFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortPreset, setSortPreset] = useState(DEFAULT_LLM_SORT_PRESET);
  const [tableFunctions, setTableFunctions] = useState<tableFunctionsProps>({
    limit: Number(process.env.REACT_APP_ROWS_PER_PAGE) || 10,
    offset: 0,
    search: '',
    sort_col: 'name',
    sort_order: 'desc',
  });
  const [dataToDelete, setDataToDelete] = useState<Array<number>>([]);
  const [providers, setProviders] = useState<Array<providersTypes>>([]);
  const [models, setModels] = useState<Array<modelsTypes>>([]);
  const [dataUpdated, setDataUpdated] = useState<boolean>(false);
  const [loading, setLoading] = useState(true);
  const [llmCount, setLlmCount] = useState<number>(0);

  // users who have access can view it
  useEffect(() => {
    const hasAccess = verifyAccess('llm', '/llm');
    if (!hasAccess) {
      navigate('/unauthorized');
    }
  }, [navigate]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      getProviders()
        .then((res) => {
          if (res.status === 200) {
            setProviders(res.data?.data || []);
          } else if (res.status === 401) {
            setLogout(true);
          }
        })
        .catch(() => {
          setShowError(true);
          makeToast('error', 'Something went wrong!');
        });
    }, 0);
    return () => clearTimeout(timeoutId);
  }, [dataUpdated]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      getModels({
        ...tableFunctions,
        provider_id:
          providerFilter !== 'all' && !Number.isNaN(Number(providerFilter))
            ? Number(providerFilter)
            : undefined,
        model_status: statusFilter !== 'all' ? statusFilter : undefined,
      })
        .then((res) => {
          if (res.status === 200) {
            setModels(res.data?.data.models || []);
            setLlmCount(res.data?.data.total_models || 0);
            setLoading(false);
          } else if (res.status === 401) {
            setLogout(true);
          }
        })
        .catch(() => {
          makeToast('error', 'Something went wrong!');
          setShowError(true);
        });
    }, 0);
    return () => clearTimeout(timeoutId);
  }, [dataUpdated, tableFunctions, providerFilter, statusFilter]);

  const llmColumns = useMemo((): ColumnDef<modelsTypes>[] => {
    return [
      {
        key: 'model',
        header: 'Model Name',
        width: 'w-[28%]',
        headerIcon: (
          <div className="w-10 h-10 rounded-full bg-[#e0f1f8] flex items-center justify-center flex-shrink-0">
            <Cpu className="w-4 h-4 text-[#0A8BC7]" />
          </div>
        ),
        render: (row: modelsTypes) => {
          const modelName = row.name ?? '';
          return (
            <div className="flex items-center gap-3 min-w-0 ml-2">
              <div className="w-10 h-10 rounded-full bg-[#e0f1f8] flex items-center justify-center flex-shrink-0">
                <Cpu className="w-4 h-4 text-[#0A8BC7]" />
              </div>
              <div className="min-w-0 flex-1">
                <Tooltip title={modelName}>
                  <p className="font-medium text-gray-900 truncate">
                    {truncateData(modelName, 25)}
                  </p>
                </Tooltip>
              </div>
            </div>
          );
        },
      },
      {
        key: 'provider',
        header: 'Provider',
        width: 'w-[11%]',
        render: (row: modelsTypes) => {
          const providerName = row.provider_name || '—';
          return (
            <Tooltip title={providerName}>
              <span className="block truncate">{truncateData(providerName, 15)}</span>
            </Tooltip>
          );
        },
      },
      {
        key: 'model_score',
        header: 'Score',
        width: 'w-[8%]',
        align: 'center',
        render: (row: modelsTypes) => {
          const score = row.model_score;
          return (
            <span className="font-medium text-[#6A6A6A] tabular-nums">
              {score !== undefined && score !== null ? Number(score).toFixed(2) : '—'}
            </span>
          );
        },
      },
      {
        key: 'routing_band',
        header: 'Band',
        width: 'w-[6%]',
        align: 'center',
        render: (row: modelsTypes) => {
          const band = displayRoutingBand(row);
          if (band == null) {
            return <span className="text-[#6A6A6A]">—</span>;
          }
          return (
            <Tooltip title={ROUTING_BAND_SCORE_HINT}>
              <span className="inline-flex items-center justify-center min-w-[1.25rem] text-xs font-semibold tabular-nums px-1.5 py-0.5 rounded bg-[#E0F1F8] text-[#132961] border border-[#0A8BC7]/20 whitespace-nowrap">
                {band}
              </span>
            </Tooltip>
          );
        },
      },
      {
        key: 'model_status',
        header: 'Status',
        width: 'w-[9%]',
        align: 'center',
        render: (row: modelsTypes) => {
          const status = row.model_status ?? 'testing';
          let tone = 'text-[#6A6A6A] bg-[#F4F4F5]';
          if (status === 'active') {
            tone = 'text-emerald-700 bg-emerald-50';
          } else if (status === 'disabled' || status === 'deprecated') {
            tone = 'text-amber-800 bg-amber-50';
          }
          return (
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${tone}`}>{status}</span>
          );
        },
      },
      {
        key: 'input_pricing',
        header: 'Input Price',
        width: 'w-[10%]',
        align: 'center',
        render: (row: modelsTypes) => {
          const price = row.input_price_per_million ?? row.input_token_pricing;
          return <span className="font-medium text-[#6A6A6A]">${price?.toFixed(2) ?? '0'}</span>;
        },
      },
      {
        key: 'output_pricing',
        header: 'Output Price',
        width: 'w-[10%]',
        align: 'center',
        render: (row: modelsTypes) => {
          const price = row.output_price_per_million ?? row.output_token_pricing;
          return <span className="font-medium text-[#6A6A6A]">${price?.toFixed(2) ?? '0'}</span>;
        },
      },
      {
        key: 'description',
        header: 'Description',
        width: 'w-[22%]',
        render: (row: modelsTypes) => {
          const desc = row.description || 'No description';
          return (
            <Tooltip title={desc}>
              <span className="block truncate text-gray-600">{truncateData(desc, 65)}</span>
            </Tooltip>
          );
        },
      },
    ];
  }, []);

  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleEditLLM = (model: modelsTypes) => {
    setDataToEdit(model as llmDetailsProps);
    setModalInfo({ modalTitle: EditLLMTitle, modalDesc: EditLLMDesc });
    setModalType('add-edit');
  };

  const handleDeleteLLM = (model: modelsTypes) => {
    setDataToDelete([model.id]);
    setModalType('delete');
  };

  const handleAddLLM = (e: React.MouseEvent<HTMLButtonElement>) => {
    e?.preventDefault();
    setModalInfo({ modalTitle: AddLLMTitle, modalDesc: AddLLMDesc });
    setDataToEdit(emptyLLM);
    setModalType('add-edit');
  };

  const handleAddProvider = () => {
    setModalType('add-provider');
  };

  const totalPages = Math.ceil(llmCount / tableFunctions.limit) || 1;
  const currentPage = Math.floor(tableFunctions.offset / tableFunctions.limit) + 1;

  const goToPage = (page: number) => {
    const offset = (page - 1) * tableFunctions.limit;
    setTableFunctions((prev) => ({ ...prev, offset }));
    setLoading(true);
  };

  const hasActiveFilters =
    searchQuery.trim() !== '' ||
    providerFilter !== 'all' ||
    statusFilter !== 'all' ||
    sortPreset !== DEFAULT_LLM_SORT_PRESET;

  const handleSortPresetChange = (preset: string) => {
    const resolved = LLM_SORT_PRESETS[preset] ?? LLM_SORT_PRESETS[DEFAULT_LLM_SORT_PRESET];
    setSortPreset(preset);
    setTableFunctions((prev) => ({
      ...prev,
      sort_col: resolved.sort_col,
      sort_order: resolved.sort_order,
      offset: 0,
    }));
    setLoading(true);
  };

  const handleClearAllFilters = () => {
    setSearchQuery('');
    setProviderFilter('all');
    setStatusFilter('all');
    setSortPreset(DEFAULT_LLM_SORT_PRESET);
    const defaultSort = LLM_SORT_PRESETS[DEFAULT_LLM_SORT_PRESET];
    setTableFunctions((prev) => ({
      ...prev,
      search: '',
      offset: 0,
      sort_col: defaultSort.sort_col,
      sort_order: defaultSort.sort_order,
    }));
    setLoading(true);
  };

  const emptyMessage = hasActiveFilters
    ? 'No LLMs match your filters. Try clearing filters.'
    : 'No LLMs found.';

  const providerFilterOptions = useMemo(
    () => [
      { value: 'all', label: 'All providers' },
      ...providers.map((p) => ({
        value: String(p.id),
        label: p.provider_name ?? `Provider ${p.id}`,
      })),
    ],
    [providers],
  );

  const sortFilterOptions = useMemo(
    () =>
      Object.entries(LLM_SORT_PRESETS).map(([value, { label }]) => ({
        value,
        label,
      })),
    [],
  );

  const {
    hasCreate: hasCreatePermission,
    hasUpdate: hasEditPermission,
    hasDelete: hasDeletePermission,
  } = getModuleCrudPermissions('llm');

  const headerLeftContent = useMemo(
    () => (
      <div className="flex flex-wrap items-center justify-between gap-4 w-full">
        <div>
          <h1 className="text-xl font-bold text-gray-900">All LLMs</h1>
          <p className="text-gray-500 text-sm mt-1">Manage language models and providers</p>
        </div>
        <div className="flex gap-2">
          {hasCreatePermission && (
            <button
              type="button"
              onClick={handleAddProvider}
              className="px-4 py-2 text-sm font-medium text-[#0A8BC7] border-1 border-[#0A8BC7] hover:bg-[#0A8BC7]/10 rounded-lg transition-colors"
            >
              Add Provider
            </button>
          )}
          {hasCreatePermission && (
            <button
              type="button"
              onClick={handleAddLLM}
              className="px-4 py-2 text-sm font-medium text-[#F48B00] border-1 border-[#F48B00] hover:bg-[#F48B00]/10 rounded-lg transition-colors"
            >
              Add LLM
            </button>
          )}
        </div>
      </div>
    ),
    [hasCreatePermission],
  );

  usePageHeader({ className: 'flex-col gap-y-[20px] justify-between', headerLeftContent });

  if (showError) {
    return (
      <div className="flex w-full h-full items-center justify-center">
        <ErrorFallback />
      </div>
    );
  }

  return (
    <>
      <div>
        {/* Single-row filters via LlmListToolbar (replaces FilterSection + band chips). */}
        <LlmListToolbar
          searchValue={searchQuery}
          onSearchChange={(v) => {
            setSearchQuery(v);
            if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
            searchTimeoutRef.current = setTimeout(() => {
              setTableFunctions((prev) => ({ ...prev, search: v, offset: 0 }));
              setLoading(true);
              searchTimeoutRef.current = null;
            }, 300);
          }}
          searchPlaceholder="Search models…"
          providerFilter={providerFilter}
          onProviderFilterChange={(v) => {
            setProviderFilter(v);
            setTableFunctions((prev) => ({ ...prev, offset: 0 }));
            setLoading(true);
          }}
          providerOptions={providerFilterOptions}
          statusFilter={statusFilter}
          onStatusFilterChange={(v) => {
            setStatusFilter(v);
            setTableFunctions((prev) => ({ ...prev, offset: 0 }));
            setLoading(true);
          }}
          statusOptions={LLM_STATUS_FILTER_OPTIONS}
          sortPreset={sortPreset}
          onSortPresetChange={handleSortPresetChange}
          sortOptions={sortFilterOptions}
          sortClearValue={DEFAULT_LLM_SORT_PRESET}
          hasActiveFilters={hasActiveFilters}
          onClearFilters={handleClearAllFilters}
          pageCount={models.length}
          totalCount={llmCount}
        />

        {/* LLMs Table */}
        <CustomTable<modelsTypes>
          columns={llmColumns}
          data={models}
          rowKey="id"
          loading={loading}
          totalCount={llmCount}
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={goToPage}
          title="LLMs"
          emptyMessage={emptyMessage}
          loadingMessage="Loading LLMs..."
          actions={{
            onEdit: (row: modelsTypes) => hasEditPermission && handleEditLLM(row),
            onDelete: (row: modelsTypes) => hasDeletePermission && handleDeleteLLM(row),
            canEdit: () => hasEditPermission,
            canDelete: () => hasDeletePermission,
          }}
        />
      </div>

      <CreateOrUpdateLLM
        modalTitle={modalInfo.modalTitle}
        modalDesc={modalInfo.modalDesc}
        open={modalType === 'add-edit'}
        setOpen={(open) => setModalType(open ? 'add-edit' : 'none')}
        loading={loading}
        dataToEdit={dataToEdit}
        setDataUpdated={setDataUpdated}
        providers={providers}
      />
      <DeleteModel
        open={modalType === 'delete'}
        setDeleteLLMModalOpen={(open) => setModalType(open ? 'delete' : 'none')}
        dataToDelete={dataToDelete}
        setDataUpdated={setDataUpdated}
        setDataToDelete={setDataToDelete}
      />
      <AddProvider
        openProviderModal={modalType === 'add-provider'}
        setOpenProviderModal={(open) => setModalType(open ? 'add-provider' : 'none')}
        setDataUpdated={setDataUpdated}
      />
      {logout && createPortal(<Logout />, document.body)}
    </>
  );
}

export default LLMModule;
